import axios from 'axios';
import qs from 'qs'; // 用于方便地将对象转换为对应格式的字符串

/**
 * 封装 Axios 请求的类
 */
class AxiosRequest {
    /**
     * 构造函数，接收一个 baseURL 参数，用于设置请求的基础 URL
     * @param {string} baseURL - 请求的基础 URL
     */
    constructor(baseURL) {
        // 创建 axios 实例，设置基础 URL 等通用配置
        this.instance = axios.create({
            baseURL: baseURL,
            timeout: 5 * 60 * 1000, // 超时时间可根据实际情况调整
            withCredentials: true // 允许跨域请求携带凭证（如 cookie）
        });

        // 设置请求拦截器
        this.instance.interceptors.request.use(
            /**
             * 请求成功时的拦截处理函数
             * @param {Object} config - 请求配置对象
             * @returns {Object} - 处理后的请求配置对象
             */
            config => {
                // 从本地存储中获取 token
                const token = localStorage.getItem('token');
                // 如果存在 token，则将其添加到请求头的 Authorization 字段中
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                // 判断数据类型
                if (config.data instanceof FormData) {
                    // 文件上传
                    // console.log('拦截器-文件上传')
                    config.headers['Content-Type'] = 'multipart/form-data';
                } else if (typeof config.data === 'object') {
                    // 表单数据
                    // console.log('拦截器-表单数据',typeof config.data)
                    config.data = qs.stringify(config.data);// 将数据序列化为URL码格式，适配php用$POST接收
                    config.headers['Content-Type'] = 'application/x-www-form-urlencoded';
                } else {
                    // json 字符串
                    // console.log('拦截器-json 字符串',typeof config.data)
                    config.headers['Content-Type'] = 'application/json;charset=utf-8';
                }
                return config;
            },
            /**
             * 请求失败时的拦截处理函数
             * @param {Error} error - 请求错误对象
             * @returns {Promise<Error>} - 拒绝该请求并抛出错误
             */
            error => {
                return Promise.reject(error);
            }
        );

        // 设置响应拦截器
        this.instance.interceptors.response.use(
            /**
             * 响应成功时的拦截处理函数
             * @param {Object} response - 响应对象
             * @returns {Object} - 只返回响应中的数据部分
             */
            response => {
                // 可以在这里对响应数据进行统一处理，例如直接返回数据部分
                return response.data;
            },
            /**
             * 响应失败时的拦截处理函数
             * @param {Error} error - 响应错误对象
             * @returns {Promise<Error>} - 拒绝该请求并抛出错误
             */
            error => {
                // 统一处理响应错误，根据不同的状态码进行相应操作
                if (error.response) {
                    const statusCode = error.response.status;
                    switch (statusCode) {
                        case 400:
                            console.log('请求参数错误');
                            break;
                        case 401:
                            console.log('未授权，请登录');
                            break;
                        case 403:
                            console.log('权限不足');
                            break;
                        case 404:
                            console.log('请求资源不存在');
                            break;
                        case 500:
                            console.log('服务器内部错误');
                            break;
                        default:
                            console.log(`请求出错，状态码：${statusCode}`);
                    }
                } else if (error.request) {
                    console.log('网络连接异常，请检查网络');
                } else {
                    console.log('请求配置出错', error.message);
                }
                return Promise.reject(error);
            }
        );
    }

    /**
     * 定义一个异步的 get 请求方法
     * @param {string} url - 请求的 URL
     * @param {Object} [params={}] - 请求参数
     * @param {Object} [config={}] - 请求配置对象
     * @returns {Promise<Object>} - 响应数据的 Promise 对象
     */
    async get(url, params = {}, config = {}) {
        try {
            // 发起 get 请求，并传入 URL、参数和配置对象
            const response = await this.instance.get(url, {
                params,
               ...config
            });
            // 返回响应数据
            return response;
        } catch (error) {
            // 捕获请求过程中的错误并抛出
            throw error;
        }
    }

    /**
     * 定义一个异步的 post 请求方法
     * @param {string} url - 请求的 URL
     * @param {Object} [data={}] - 请求数据
     * @param {Object} [config={}] - 请求配置对象
     * @returns {Promise<Object>} - 响应数据的 Promise 对象
     */
    async post(url, data = {}, config = {}) {
        try {
            // 发起 post 请求，并传入 URL、数据和配置对象
            const response = await this.instance.post(url, data, {
               ...config
            });
            // 返回响应数据
            return response;
        } catch (error) {
            // 捕获请求过程中的错误并抛出
            throw error;
        }
    }

    /**
     * 定义一个异步的 put 请求方法
     * @param {string} url - 请求的 URL
     * @param {Object} [data={}] - 请求数据
     * @param {Object} [config={}] - 请求配置对象
     * @returns {Promise<Object>} - 响应数据的 Promise 对象
     */
    async put(url, data = {}, config = {}) {
        try {
            // 发起 put 请求，并传入 URL、数据和配置对象
            const response = await this.instance.put(url, data, {
               ...config
            });
            // 返回响应数据
            return response;
        } catch (error) {
            // 捕获请求过程中的错误并抛出
            throw error;
        }
    }

    /**
     * 定义一个异步的 delete 请求方法
     * @param {string} url - 请求的 URL
     * @param {Object} [params={}] - 请求参数
     * @param {Object} [config={}] - 请求配置对象
     * @returns {Promise<Object>} - 响应数据的 Promise 对象
     */
    async delete(url, params = {}, config = {}) {
        try {
            // 发起 delete 请求，并传入 URL、参数和配置对象
            const response = await this.instance.delete(url, {
                params,
               ...config
            });
            // 返回响应数据
            return response;
        } catch (error) {
            // 捕获请求过程中的错误并抛出
            throw error;
        }
    }

    /**
     * 定义一个异步的上传文件方法
     * @param {string} url - 请求的 URL
     * @param {File} file - 要上传的文件
     * @param {Object} [config={}] - 请求配置对象
     * @returns {Promise<Object>} - 响应数据的 Promise 对象
     */
    async uploadFile(url, file, config = {}) {
        try {
            // 创建一个 FormData 对象
            const formData = new FormData();
            // 将文件添加到 FormData 中
            formData.append('file', file);
            // 配置请求头，设置 Content-Type 为 multipart/form-data
            const defaultConfig = {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            };
            // 发起 post 请求上传文件
            const response = await this.instance.post(url, formData, {
               ...defaultConfig,
               ...config
            });
            // 返回响应数据
            return response;
        } catch (error) {
            // 捕获请求过程中的错误并抛出
            throw error;
        }
    }
}

// 实例化
const axiosApi = new AxiosRequest('');

// 暴露模块
export default axiosApi;